import React, { ReactElement, useCallback, useId, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import { useControlled } from '../../hooks/useControlled';
import { useRover } from '../../hooks/useRover';
import { IconProps } from '../icons/generated';
import { getSegmentedControlStyleByProps, getSegmentedControlGroupStyleByProps } from './styles';
import { getKeyFromItem } from '../../utils/shared';
import { AriaFocusProps } from '../../types/common';
import { SegmentedControlSize } from './types';
import { SegmentedControlOption } from './SegmentedControlOption';
import { SegmentedControlThumb } from './SegmentedControlThumb';
import { TooltipProps } from '../tooltip';

export interface SegmentedControlOptionType {
  label?: string;
  value: string;
  icon?: ReactElement<IconProps>;
  disabled?: boolean;
  tooltipProps?: Pick<TooltipProps, 'openerTriggerEvent' | 'placement'>;
  /**
   * IconOnly인 경우 노출됩니다.
   */
  tooltip?: string;
  /**
   * disabled 상태일때 노출됩니다.
   * iconOnly인 경우 {tooltip} ({disabledTooltip}) 형태로 노출됩니다.
   */
  disabledTooltip?: string;
}

export interface SegmentedControlProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'value' | 'onChange'>,
    AriaFocusProps {
  className?: string;
  defaultValue?: string;
  value?: string;
  size?: SegmentedControlSize;
  items: SegmentedControlOptionType[];
  width?: 'fill' | 'auto';
  disabled?: boolean;
  keyExtractor?: (item: SegmentedControlOptionType, index: number) => string | number;
  onChange?: (value: string) => void;
}

export interface SegmentedControlInstance {
  value?: string;
  focus: () => void;
}

export const SegmentedControl = React.forwardRef(
  (
    {
      className,
      size = 'medium',
      items,
      defaultValue,
      value,
      disabled = false,
      width = 'auto',
      keyExtractor = getKeyFromItem,
      onChange,
      ...other_props
    }: SegmentedControlProps,
    ref: React.Ref<SegmentedControlInstance>,
  ) => {
    const [focused_index, setFocusedIndex] = useState(0);
    const [checked_value, setCheckedValue] = useControlled<string | undefined>({
      defaultValue,
      value,
    });
    const [thumb_show, setThumbShow] = useState(false);
    const first_ref = useRef<HTMLInputElement | null>(null);
    const [segment_ref, setSegmentRef] = useState<HTMLDivElement | null>(null);
    const name = useId();

    const handleChange = useCallback(
      (item_value: string) => {
        setCheckedValue(item_value);
        onChange?.(item_value);
      },
      [onChange, setCheckedValue],
    );

    const { getRoverItemProps } = useRover({
      index: focused_index,
      onIndexChange: (index, is_selecting) => {
        setFocusedIndex(index);
        if (is_selecting) {
          handleChange(items[index].value);
        }
      },
      length: items.length,
    });

    useImperativeHandle(
      ref,
      () => ({
        value: checked_value,
        focus: () => first_ref.current?.focus?.(),
      }),
      [checked_value],
    );

    const checked_index = items.findIndex((item) => item.value === checked_value);

    return (
      <div
        ref={setSegmentRef}
        className={classNames('pds-segmented-control', className)}
        css={getSegmentedControlStyleByProps({ size, width, disabled })}
        role='radiogroup'
        {...other_props}
      >
        <div className='pds-segmented-control-group' css={getSegmentedControlGroupStyleByProps({ size, width })}>
          {segment_ref && (
            <SegmentedControlThumb
              containerRef={segment_ref}
              checkedIndex={checked_index}
              updatedKey={`${size}_${width}`}
              onMotionStart={() => setThumbShow(true)}
              onMotionEnd={() => setThumbShow(false)}
            />
          )}
          {items.map((item, index) => {
            const checked = checked_value === item.value;
            return (
              <SegmentedControlOption
                name={name}
                key={keyExtractor(item, index)}
                label={item.label}
                icon={item.icon}
                tooltip={item.tooltip}
                tooltipProps={item.tooltipProps}
                checked={checked}
                disabledTooltip={item.disabledTooltip}
                disabled={disabled || item.disabled}
                onClick={() => handleChange(item.value)}
                size={size}
                width={width}
                thumbShow={thumb_show}
                {...getRoverItemProps(index, {
                  ref: index === 0 ? first_ref : undefined,
                })}
              />
            );
          })}
        </div>
      </div>
    );
  },
);
